import { Button, Modal, Space, Upload, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import React, { useCallback } from 'react';

import BulkVendorCodeCommercialTable from './../../vendor/tables/BulkVendorCodeCommercialTable';
import RequestErrorAlert from '../../shared/RequestErrorAlert';
import type { TVendorCodeCommercialUpsertModel } from './../../../models/vendor/VendorCodeModels';
import { VendorCodeType } from '../../../enums/vendor/VendorCodeTypeEnum';
import config from '../../../config';
import { convertKilobytesToBytes } from '../../../utils/bytesUtil';
import { getKiloBytesDisplayValue } from '../../../utils/displayUtils';
import useParseVendorCodesCommercialHomeWarrantyCsvFile from './../../../hooks/services/vendor/useParseVendorCodesCommercialHomeWarrantyCsvFile';
import useParseVendorCodesCommercialCsvFile from './../../../hooks/services/vendor/useParseVendorCodesCommercialCsvFile';
import useStatusNotifications from './../../../hooks/shared/useStatusNotifications';
import useUpsertVendorCodesCommercialHomeWarranty from './../../../hooks/services/vendor/useUpsertVendorCodesCommercialHomeWarranty';
import useUpsertVendorCodesCommercial from './../../../hooks/services/vendor/useUpsertVendorCodesCommercial';

type Props = {
  isShown: boolean;
  setIsShown: (isShown: boolean) => void;
  vendorCodeType: VendorCodeType.COMMERCIAL | VendorCodeType.COMMERCIAL_HOME_WARRANTY;
};

const BulkVendorCodeCommercialModalForm = ({ isShown, setIsShown, vendorCodeType }: Props) => {
  const { Dragger } = Upload;
  const maxFileSizeKb = config.maxBulkUpdateFileSizeKb;
  const commercialCsvFile = useParseVendorCodesCommercialCsvFile();
  const commercialHomeWarrantyCsvFile = useParseVendorCodesCommercialHomeWarrantyCsvFile();
  const commercialUpsert = useUpsertVendorCodesCommercial();
  const commercialHomeWarrantyUpsert = useUpsertVendorCodesCommercialHomeWarranty();
  const csvFile =
    vendorCodeType === VendorCodeType.COMMERCIAL
      ? commercialCsvFile
      : commercialHomeWarrantyCsvFile;
  const vendorCodeUpsert =
    vendorCodeType === VendorCodeType.COMMERCIAL ? commercialUpsert : commercialHomeWarrantyUpsert;
  const [data, setData] = React.useState<Array<TVendorCodeCommercialUpsertModel>>([]);
  const [fileErrorMessage, setFileErrorMessage] = React.useState<string>();

  const onDownloadTemplate = useCallback(() => {
    downloadBulkVendorCodeTemplate(vendorCodeType);
  }, [vendorCodeType]);

  const handleFileUpload = useCallback(
    async (file: File) => {
      csvFile.upload(file, {
        onSuccess: (data) => setData(data ?? []),
      });
    },
    [csvFile, setData],
  );

  const onSubmit = useCallback(async () => {
    vendorCodeUpsert.upsert(data, {
      onSuccess: () => {
        setFileErrorMessage('');
        setIsShown(false);
        setData([]);
      },
    });
  }, [vendorCodeUpsert, data, setFileErrorMessage, setIsShown, setData]);

  const onCancel = useCallback(() => {
    setFileErrorMessage('');
    setIsShown(false);
    setData([]);
  }, [setFileErrorMessage, setIsShown, setData]);

  const draggerProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    onChange(info) {
      const { status, response } = info.file;
      if (status === 'done') {
        message.success(`${info.file.name} file uploaded successfully.`);
        setData(response);
      } else if (status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    beforeUpload: (file) => {
      const allowedExtensions = /(\.csv)$/i;
      if (!allowedExtensions.exec(file.name.toLowerCase())) {
        setFileErrorMessage(`'${file.name}' has an invalid file type. Please select a .csv file.`);
        return false;
      }

      if (file.size > convertKilobytesToBytes(maxFileSizeKb)) {
        setFileErrorMessage(
          `'${file.name}' is too large. Please select a file within the max file size.`,
        );
        return false;
      }
      handleFileUpload(file);
      return false;
    },
  };

  useStatusNotifications({
    isSuccess: !!vendorCodeUpsert.isSuccess,
    isError: !!vendorCodeUpsert.isError,
    successDescription: 'Vendor code records updated successfully.',
  });

  return (
    <Modal
      onOk={onSubmit}
      onCancel={onCancel}
      title={getTitle(vendorCodeType)}
      open={isShown}
      width={900}
      okText={'Update'}
      okButtonProps={{ disabled: data.length === 0, loading: vendorCodeUpsert.isLoading }}
    >
      <RequestErrorAlert isError={vendorCodeUpsert.isError} />
      {data.length === 0 && (
        <>
          {fileErrorMessage && <RequestErrorAlert isError={true} description={fileErrorMessage} />}
          <Space className="marginBottom12">
            <Button icon={<DownloadOutlined />} onClick={onDownloadTemplate}>
              Download CSV Template
            </Button>
          </Space>
          <Dragger {...draggerProps}>
            <div className="invoiceUploadHorizontalSection">
              <div className="invoiceUploadBoxContents">
                <div>
                  Drag and drop CSV file here or <a>Upload</a>
                </div>
                <div>Max file size: {getKiloBytesDisplayValue(maxFileSizeKb)}</div>
              </div>
            </div>
          </Dragger>
        </>
      )}
      {data.length > 0 && <BulkVendorCodeCommercialTable data={data} />}
    </Modal>
  );
};

const BULK_VENDOR_CODE_TEMPLATE_HEADERS = [
  'Row Changed',
  'Id',
  'Vendor Id',
  'Marketing Campaign Id',
  'Name',
  'Code',
  'Toll free number',
  'Marketing Tier',
  'Creative',
  'Media Type',
  'Station',
  'Is Active',
];

const BULK_VENDOR_CODE_TEMPLATE_EXAMPLES = [
  [
    'y',
    '',
    '123',
    '1',
    'Example New Code',
    'EXAMPLE1',
    '8005550101',
    '1',
    'Digital',
    'Web',
    '',
    'true',
  ],
  [
    'y',
    '456',
    '123',
    '4',
    'Example Existing Code',
    'EXAMPLE2',
    '8005550102',
    '3',
    'Mail',
    'DM',
    'WXYZ',
    'true',
  ],
];

function downloadBulkVendorCodeTemplate(
  vendorCodeType: VendorCodeType.COMMERCIAL | VendorCodeType.COMMERCIAL_HOME_WARRANTY,
): void {
  const rows = [BULK_VENDOR_CODE_TEMPLATE_HEADERS, ...BULK_VENDOR_CODE_TEMPLATE_EXAMPLES];
  const csvContent = `${rows.map((row) => row.map(escapeCsvValue).join(',')).join('\r\n')}\r\n`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = getTemplateFileName(vendorCodeType);
  link.click();
  URL.revokeObjectURL(url);
}

function escapeCsvValue(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function getTemplateFileName(
  vendorCodeType: VendorCodeType.COMMERCIAL | VendorCodeType.COMMERCIAL_HOME_WARRANTY,
): string {
  return vendorCodeType === VendorCodeType.COMMERCIAL
    ? 'commercial-vendor-code-bulk-template.csv'
    : 'commercial-home-warranty-vendor-code-bulk-template.csv';
}

function getTitle(
  vendorCodeType: VendorCodeType.COMMERCIAL | VendorCodeType.COMMERCIAL_HOME_WARRANTY,
): string {
  return vendorCodeType === VendorCodeType.COMMERCIAL
    ? 'Bulk Vendor Code Update - Commercial'
    : 'Bulk Vendor Code Update - Commercial Home Warranty';
}

export default BulkVendorCodeCommercialModalForm;
